import classNames from "classnames";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { flattenChildren } from "client/Misc/reactUtils";
import {
	Children,
	isValidElement,
	PropsWithChildren,
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	FontIconName,
	IconValue,
	SvgIconValue
} from "../Icon/Icon";
import styles from "./Tabs.module.less";
import { TabsNavigation, TabsNavigationItemData } from "../TabsNavigation/TabsNavigation";

type Child<TTabId extends string> = ReactElement<TabProps<TTabId>> | false;

export type TabsProps<TTabId extends string> = {
	children: Child<TTabId> | Child<TTabId>[];
	height?: "auto" | "fill";
	activeTabId?: TTabId;
	onActiveTabIdChange?(tabId: TTabId): void;
};

export const Tabs = <TTabId extends string = string,>(props: TabsProps<TTabId>): ReactElement => {
	const {
		children,
		height = "auto",
		activeTabId: providedActiveTabId,
		onActiveTabIdChange
	} = props;

	const arrayChildren = useMemo(
		(): readonly ReactElement<TabProps<TTabId>>[] => flattenChildren(
			Children.map(children, x => x)
				.filter(isValidElement)
		),
		[children]
	);

	const [managedActiveTabId, setManagedActiveTabId] = useState<TTabId | undefined>(arrayChildren[0]?.props.id);

	const tabItems = useMemo(
		(): readonly TabsNavigationItemData<TTabId>[] => {
			return arrayChildren.map(
				(x): TabsNavigationItemData<TTabId> => {
					return {
						value: x.props.id,
						text: x.props.title,
						icon: x.props.icon,
						rightIcon: x.props.hasValidationError
							? validationIcon
							: undefined,
						trackingId: generateTrackingId("tab-header", `_${x.props.id}`)
					};
				}
			);
		},
		[arrayChildren]
	);

	const onActivateHandler = (id: TTabId): void => {
		setManagedActiveTabId(id);
		if (onActiveTabIdChange) {
			onActiveTabIdChange(id);
		}
	};

	const activeTabId: TTabId | undefined = providedActiveTabId ?? managedActiveTabId;

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_fillHeight]: height === "fill"
				}
			)}
		>
			<div className={styles.header}>
				<TabsNavigation
					items={tabItems}
					onChange={onActivateHandler}
					value={activeTabId}
					trackingId="tab-header"
				/>
			</div>

			<ul className={styles.content}>
				{arrayChildren.map((x) => (
					<li
						key={x.props.id}
						className={classNames(
							styles.tabPanel,
							{
								[styles.tabPanel_active]: x.props.id === activeTabId
							}
						)}
						{...setTrackingId("tab-content", `_${x.props.id}`)}
					>
						{x}
					</li>
				))}
			</ul>
		</div>
	);
};
Tabs.displayName = "Tabs";

export type TabProps<TTabId extends string = string> = {
	id: TTabId;
	title: string;
	hasValidationError?: boolean;
	icon?: FontIconName | SvgIconValue;
};

const Tab = <TTabId extends string = string,>(props: PropsWithChildren<TabProps<TTabId>>): ReactElement => {
	const {
		children
	} = props;

	return (
		<>
			{children}
		</>
	);
};
Tab.displayName = "Tabs.Tab";

Tabs.Tab = Tab;

const validationIcon: IconValue = {
	kind: "font",
	name: "triangle-exclamation",
	color: "danger"
};
